import { Link, UserDisplayName } from "@/components";
import { GetSearchFilterProps } from "@/components/TableFilters/components/GetSearchFilterProps";
import {
  EntityStatus,
  FieldNames,
  FormType,
} from "@/enums";
import {
  buildProjectStatusOptions,
  generateDropDownOptions,
  getOrderBy,
  getProjectStatusTranslationId,
  getSortOrder,
  routes,
} from "@/helpers";
import { projectDashboardType, projectListType } from "@/propTypes";
import { useFormPropertyValueLookupProvider } from "@/providers";
import {
  useGetProductCategoryFormsQuery,
  useGetUsersByIdsMutation,
} from "@/services";
import { Pager } from "@/sharedStyled";
import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Table, Tooltip } from "antd";
import _ from "lodash";
import {
  arrayOf,
  bool,
  exact,
  func,
  number,
  objectOf,
  oneOfType,
  string,
} from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { GetDropDownFilterProps } from "../TableFilters/components/GetDropDownFilterProps";
import { GetUserFilterProps } from "../TableFilters/components/GetUserFilterProps";
import { PROJECTS_TABLE_COLUMNS } from "./columns";
import { DescriptionCol, NameCol } from "./styled";

const PAGINATION_PAGE_SIZE = 10;
const DEFAULT_PAGE = 1;

const ProjectsTable = ({
  columnsToDisplay,
  customColumns = [],
  projects,
  loading,
  scroll,
  totalCount,
  queryParams,
  setQueryParams,
}) => {
  const intl = useIntl();
  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();
  const [sortOrder, setSortOrder] = useState({});
  const [projectForm, setProjectForm] = useState(null);

  const { data: forms } = useGetProductCategoryFormsQuery();
  const [getUsersByIds, { data: usersByIds }] = useGetUsersByIdsMutation();

  useEffect(() => {
    setProjectForm(forms?.intake);
  }, [forms?.intake]);

  useEffect(() => {
    setSortOrder(getSortOrder(queryParams.orderBy));
  }, [queryParams]);

  useEffect(() => {
    const projectLeaderIds =
      projects?.flatMap(({ projectLeaders }) =>
        projectLeaders?.flatMap(({ id }) => id)
      ) || [];
    const requestorIds = projects?.map(({ requestor }) => requestor) || [];

    const userIds = [...projectLeaderIds, ...requestorIds];

    if (userIds?.length) {
      const uniqueProjectLeaderIds = [...new Set(userIds)];
      getUsersByIds(uniqueProjectLeaderIds);
    }
  }, [projects, getUsersByIds]);

  /***
   * handleChange updates the orderBy query parameter.
   * We are ignoring pagination and filters, as we are handling those manually on the column itself.
   * @param pagination
   * @param filters
   * @param sorter
   */
  const handleChange = (pagination, filters, sorter) => {
    handleSort(sorter);
  };

  const handleSort = (sorter) => {
    setQueryParams(getOrderBy(sorter));
  };

  /***
   * updateQueryParam updates a single param in the query object
   * @param key: the query param to update
   * @param value: the new value of the query param
   */
  const updateQueryParam = (key, value) => {
    const updatedQuery = { ...queryParams, page: 1 };
    updatedQuery[key] = value;
    setQueryParams(updatedQuery);
  };

  const getProjectLeadersDisplay = (projectLeaders) => {
    if (!projectLeaders?.length) return;

    const projectLeaderIds = projectLeaders.map(({ id }) => id);
    const userData = usersByIds?.filter(({ id }) =>
      projectLeaderIds.includes(id)
    );

    if (!userData?.length) return;

    return userData.map((user, index) => (
      <div key={`project-leader-${index}`}>
        <UserDisplayName
          displayName={user.displayName}
          userAccountEnabled={user.azureAdAccountEnabled}
          user={user}
        />
      </div>
    ));
  };

  const getRequestorDisplay = (requestor) => {
    if (!requestor) return null;

    const userData = usersByIds?.find(({ id }) => id === requestor);

    if (!userData) return null;

    return (
      <UserDisplayName
        displayName={userData.displayName}
        userAccountEnabled={userData.azureAdAccountEnabled}
        user={userData}
      />
    );
  };

  const buildColumn = (columnName) => {
    let column;
    let isSortable;

    const configureColumnSorting = (key) => {
      if (!sortOrder) return;

      column = { ...column, key: key, sortOrder: sortOrder[key] };
      isSortable = true;
    };

    switch (columnName) {
      case PROJECTS_TABLE_COLUMNS.intake:
        column = {
          title: intl.formatMessage({ id: "project.intake" }),
          render: (project) => (
            <Button
              data-test-id={`project-table-intake-link-${project.id}`}
              type="link"
            >
              <Link to={routes.projects.intakeView(project.id)}>
                {intl.formatMessage({ id: "project.view" })}
              </Link>
            </Button>
          ),
        };
        break;
      case PROJECTS_TABLE_COLUMNS.projectNumber:
        column = {
          title: intl.formatMessage({ id: "projects.projectNumber" }),
          render: ({ id, projectNumber }) => (
            <Button data-test-id={`project-table-link-${id}`} type="link">
              <Link to={routes.projects.get(id)}>{projectNumber || "n/a"}</Link>
            </Button>
          ),
          ...GetSearchFilterProps({
            setQueryParamFunction: updateQueryParam,
            queryParamKey: _.camelCase(FieldNames.ProjectNumber),
            queryParamValue: queryParams.projectNumber,
          }),
        };
        configureColumnSorting(FieldNames.ProjectNumber);

        break;
      case PROJECTS_TABLE_COLUMNS.projectName:
        column = {
          title: intl.formatMessage({ id: "projects.projectName" }),
          render: ({ projectName, secureProject }) => (
            <NameCol>
              {secureProject && (
                <Tooltip
                  title={intl.formatMessage({ id: "project.isSecureMessage" })}
                  trigger={["hover"]}
                >
                  <FontAwesomeIcon icon={faLock} className="mr-2" />
                </Tooltip>
              )}
              {projectName}
            </NameCol>
          ),
          ...GetSearchFilterProps({
            setQueryParamFunction: updateQueryParam,
            queryParamKey: _.camelCase(FieldNames.ProjectName),
            queryParamValue: queryParams.projectName,
          }),
        };

        configureColumnSorting(FieldNames.ProjectNameSort);

        break;
      case PROJECTS_TABLE_COLUMNS.description:
        column = {
          title: intl.formatMessage({ id: "projects.description" }),
          className: "ant-table-column-has-filter",
          render: ({ description }) => (
            <DescriptionCol>{description}</DescriptionCol>
          ),
          ...GetSearchFilterProps({
            setQueryParamFunction: updateQueryParam,
            queryParamKey: _.camelCase(FieldNames.Description),
            queryParamValue: queryParams.description,
          }),
        };

        break;
      case PROJECTS_TABLE_COLUMNS.projectType:
        column = {
          title: intl.formatMessage({ id: "projects.projectType" }),
          render: ({ projectType }) =>
            getPropertyValueDisplay(
              FormType.Intake,
              FieldNames.ProjectType,
              projectType
            ),
          ...GetDropDownFilterProps({
            setQueryParamFunction: updateQueryParam,
            queryParamKey: _.camelCase(FieldNames.ProjectType),
            queryParamValue: queryParams.projectType,
            options: generateDropDownOptions(
              FieldNames.ProjectType,
              projectForm,
              EntityStatus
            ),
          }),
        };

        configureColumnSorting(FieldNames.ProjectTypeLabel);

        break;
      case PROJECTS_TABLE_COLUMNS.status:
        column = {
          title: intl.formatMessage({ id: "projects.status" }),
          render: ({ status }) =>
            intl.formatMessage({ id: getProjectStatusTranslationId(status) }),
          ...GetDropDownFilterProps({
            setQueryParamFunction: updateQueryParam,
            queryParamKey: _.camelCase(FieldNames.Status),
            queryParamValue: queryParams.status,
            options: buildProjectStatusOptions().map((option) => ({
              ...option,
              fallback: intl.formatMessage({ id: option.translationId }),
            })),
          }),
        };

        configureColumnSorting(FieldNames.StatusLabel);

        break;
      case PROJECTS_TABLE_COLUMNS.productCategory:
        column = {
          title: intl.formatMessage({ id: "projects.productCategory" }),
          render: ({ productCategory }) =>
            getPropertyValueDisplay(
              FormType.Intake,
              FieldNames.ProductCategory,
              productCategory
            ),
          ...GetDropDownFilterProps({
            setQueryParamFunction: updateQueryParam,
            queryParamKey: _.camelCase(FieldNames.ProductCategory),
            queryParamValue: queryParams.productCategory,
            options: generateDropDownOptions(
              FieldNames.ProductCategory,
              projectForm,
              EntityStatus
            ),
          }),
        };

        configureColumnSorting(FieldNames.ProductCategoryLabel);

        break;

      case PROJECTS_TABLE_COLUMNS.requestor:
        column = {
          title: intl.formatMessage({ id: "projects.requestor" }),
          render: ({ requestor }) => getRequestorDisplay(requestor),
          ...GetUserFilterProps({
            setQueryParamFunction: updateQueryParam,
            queryParamKey: _.camelCase(FieldNames.Requestor),
            queryParamValue: queryParams.requestor,
          }),
        };

        configureColumnSorting(FieldNames.RequestorFullName);

        break;
      case PROJECTS_TABLE_COLUMNS.platformProject:
        column = {
          title: intl.formatMessage({ id: "projects.platformProject" }),
          render: ({ platformProject }) =>
            platformProject
              ? intl.formatMessage({ id: "general.yes" })
              : intl.formatMessage({ id: "general.no" }),
          ...GetDropDownFilterProps({
            setQueryParamFunction: updateQueryParam,
            queryParamKey: _.camelCase(FieldNames.PlatformProject),
            queryParamValue: queryParams.platformProject,
            singleChoice: true,
          }),
        };

        break;
      case PROJECTS_TABLE_COLUMNS.intangibleAccountingStandard:
        column = {
          title: intl.formatMessage({
            id: "projects.intangibleAccountingStandard",
          }),
          render: ({ intangibleAccountingStandard }) =>
            intangibleAccountingStandard
              ? intl.formatMessage({ id: "general.yes" })
              : intl.formatMessage({ id: "general.no" }),
          ...GetDropDownFilterProps({
            setQueryParamFunction: updateQueryParam,
            queryParamKey: _.camelCase(FieldNames.IntangibleAccountingStandard),
            queryParamValue: queryParams.intangibleAccountingStandard,
            singleChoice: true,
          }),
        };

        break;
      case PROJECTS_TABLE_COLUMNS.productLine0:
        column = {
          title: intl.formatMessage({ id: "projects.productLine0" }),
          render: ({ productLine0 }) =>
            getPropertyValueDisplay(
              FormType.Intake,
              FieldNames.ProductLine0,
              productLine0
            ),
          ...GetDropDownFilterProps({
            setQueryParamFunction: updateQueryParam,
            queryParamKey: _.camelCase(FieldNames.ProductLine0),
            queryParamValue: queryParams.productLine0,
            options: generateDropDownOptions(
              FieldNames.ProductLine0,
              projectForm,
              EntityStatus
            ),
          }),
        };

        configureColumnSorting("productLineLabel0");

        break;
      case PROJECTS_TABLE_COLUMNS.productLine1:
        column = {
          title: intl.formatMessage({ id: "projects.productLine1" }),
          render: ({ productLine1 }) =>
            getPropertyValueDisplay(
              FormType.Intake,
              FieldNames.ProductLine1,
              productLine1
            ),
          ...GetDropDownFilterProps({
            setQueryParamFunction: updateQueryParam,
            queryParamKey: _.camelCase(FieldNames.ProductLine1),
            queryParamValue: queryParams.productLine1,
            options: generateDropDownOptions(
              FieldNames.ProductLine1,
              projectForm,
              EntityStatus
            ),
          }),
        };

        configureColumnSorting("productLineLabel1");

        break;
      case PROJECTS_TABLE_COLUMNS.productLine2:
        column = {
          title: intl.formatMessage({ id: "projects.productLine2" }),
          render: ({ productLine2 }) =>
            getPropertyValueDisplay(
              FormType.Intake,
              FieldNames.ProductLine2,
              productLine2
            ),
          ...GetDropDownFilterProps({
            setQueryParamFunction: updateQueryParam,
            queryParamKey: _.camelCase(FieldNames.ProductLine2),
            queryParamValue: queryParams.productLine2,
            options: generateDropDownOptions(
              FieldNames.ProductLine2,
              projectForm,
              EntityStatus
            ),
          }),
        };

        configureColumnSorting("productLineLabel2");

        break;
      case PROJECTS_TABLE_COLUMNS.projectLeaders:
        column = {
          title: intl.formatMessage({ id: "projects.projectLeaders" }),
          render: ({ projectLeaders }) =>
            getProjectLeadersDisplay(projectLeaders),
          ...GetUserFilterProps({
            setQueryParamFunction: updateQueryParam,
            queryParamKey: _.camelCase(FieldNames.ProjectLeaders),
            queryParamValue: queryParams.projectLeaders,
          }),
        };
        break;
      case PROJECTS_TABLE_COLUMNS.estimatedLaunchDate:
        column = {
          title: intl.formatMessage({ id: "projects.estimatedLaunchDate" }),
          dataIndex: "FormattedEstimatedLaunchDate",
        };

        break;
      default:
        break;
    }

    if (isSortable)
      column = {
        ...column,
        className: "ant-table-column-has-filter",
        sorter: true,
        colConfigurator: {
          required: true,
          noFormProperty: true,
        },
      };

    return column;
  };

  return (
    <>
      <Table
        data-test-id="project-table"
        columns={[
          ...columnsToDisplay.map((column) => buildColumn(column)),
          ...customColumns,
        ]}
        dataSource={projects}
        onChange={handleChange}
        rowKey={({ id }) => id}
        pagination={false}
        loading={loading}
        scroll={scroll}
      />
      <Pager
        defaultCurrent={queryParams.page || DEFAULT_PAGE}
        current={queryParams.page || DEFAULT_PAGE}
        pageSize={PAGINATION_PAGE_SIZE}
        total={totalCount}
        showSizeChanger={false}
        onChange={(page) => setQueryParams({ page })}
        data-test-id="projects-table-pagination"
      />
    </>
  );
};

ProjectsTable.propTypes = {
  projects: arrayOf(oneOfType([projectDashboardType, projectListType])),
  loading: bool,
  scroll: exact({
    x: bool,
  }),
  columnsToDisplay: arrayOf(string).isRequired,
  customColumns: arrayOf(
    exact({
      title: string.isRequired,
      render: func.isRequired,
    })
  ),
  totalCount: number,
  queryParams: objectOf(oneOfType([number, arrayOf(string)])).isRequired,
  setQueryParams: func.isRequired,
};

export default ProjectsTable;